import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  BadgeCheck,
  CheckCircle2,
  Clipboard,
  Code2,
  Copy,
  ExternalLink,
  FileImage,
  Globe2,
  Image as ImageIcon,
  Languages,
  Link2,
  Megaphone,
  MessageSquare,
  MonitorSmartphone,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Tags,
  XCircle
} from "lucide-react";
import { sampleAudit } from "./sampleAudit";
import type { CardPreviewAudit, Level, PreviewCheck, PreviewIssue, TagMap } from "./types";
import "./styles.css";

type Language = "en" | "ko";
type OutputTab = "meta" | "markdown";

const text = {
  en: {
    product: "CardSight Preview",
    title: "Social card QA before you share",
    subtitle: "Scan Open Graph, Twitter card tags, image reachability, dimensions, and generated meta tags for public URLs.",
    url: "Public URL",
    scan: "Scan",
    scanning: "Scanning",
    sample: "Sample",
    ready: "No login. Public HTML and preview image checks only.",
    score: "Preview score",
    cards: "Platform previews",
    image: "Image diagnostics",
    checks: "QA checks",
    tags: "Scanned tags",
    output: "Generated tags",
    issues: "Priority fixes",
    copy: "Copy",
    copied: "Copied",
    meta: "Meta tags",
    markdown: "Report",
    noIssues: "No priority fixes in the current result.",
    imageAlt: "CardSight social preview QA dashboard",
    error: "The preview scan could not run.",
    noteTitle: "Cache note",
    noteBody: "Platform preview tools may cache results. Use official debuggers after publishing if a network still shows an old card."
  },
  ko: {
    product: "CardSight Preview",
    title: "공유 전 소셜 카드 QA",
    subtitle: "공개 URL의 Open Graph, Twitter 카드 태그, 이미지 접근성, 크기, 생성 메타태그를 검사합니다.",
    url: "공개 URL",
    scan: "검사",
    scanning: "검사 중",
    sample: "샘플",
    ready: "로그인 없이 공개 HTML과 프리뷰 이미지만 검사합니다.",
    score: "프리뷰 점수",
    cards: "플랫폼 미리보기",
    image: "이미지 진단",
    checks: "QA 체크",
    tags: "스캔된 태그",
    output: "생성 태그",
    issues: "우선 수정",
    copy: "복사",
    copied: "복사됨",
    meta: "메타태그",
    markdown: "리포트",
    noIssues: "현재 결과에는 우선 수정 항목이 없습니다.",
    imageAlt: "CardSight 소셜 프리뷰 QA 대시보드",
    error: "프리뷰 검사를 실행하지 못했습니다.",
    noteTitle: "캐시 참고",
    noteBody: "플랫폼별 미리보기 도구는 결과를 캐시할 수 있습니다. 배포 후 오래된 카드가 보이면 공식 디버거로 재검사를 요청하세요."
  }
};

function App() {
  const [language, setLanguage] = useState<Language>("en");
  const [targetUrl, setTargetUrl] = useState("https://metatags.io/");
  const [audit, setAudit] = useState<CardPreviewAudit>(sampleAudit);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeOutput, setActiveOutput] = useState<OutputTab>("meta");
  const t = text[language];
  const title = audit.tags.ogTitle || audit.tags.twitterTitle || audit.tags.title || audit.hostname;
  const description = audit.tags.ogDescription || audit.tags.twitterDescription || audit.tags.description || "";
  const output = activeOutput === "meta" ? audit.generated.meta : audit.generated.markdown;
  const priorityIssues = useMemo(() => audit.issues.slice(0, 6), [audit.issues]);

  async function runScan(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ url: targetUrl });
      const response = await fetch(`/api/preview?${params.toString()}`);
      const payload = (await response.json()) as CardPreviewAudit | { error?: string };
      if (!response.ok) {
        throw new Error("error" in payload ? payload.error ?? t.error : t.error);
      }
      setAudit(payload as CardPreviewAudit);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : t.error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="app-shell">
      <section className="board">
        <header className="topbar">
          <a data-google-vignette="false" className="brand" href="/" aria-label="CardSight Preview home">
            <span className="brand-mark">
              <MonitorSmartphone size={22} />
            </span>
            <span>{t.product}</span>
          </a>
          <nav aria-label="Site links">
            <a data-google-vignette="false" href="/about.html">About</a>
            <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          </nav>
          <button className="language-button" type="button" onClick={() => setLanguage(language === "en" ? "ko" : "en")}>
            <Languages size={17} />
            {language.toUpperCase()}
          </button>
        </header>

        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">
              <Megaphone size={16} />
              Open Graph / Twitter Card / Link Preview
            </p>
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
            <form className="scan-form" onSubmit={runScan}>
              <label>
                <span>{t.url}</span>
                <div className="input-shell">
                  <Globe2 size={18} />
                  <input value={targetUrl} onChange={(event) => setTargetUrl(event.target.value)} spellCheck={false} />
                </div>
              </label>
              <div className="action-row">
                <button className="secondary-button" type="button" onClick={() => setAudit(sampleAudit)}>
                  <Sparkles size={17} />
                  {t.sample}
                </button>
                <button className="primary-button" type="submit" disabled={loading}>
                  {loading ? <RefreshCw className="spin" size={17} /> : <Search size={17} />}
                  {loading ? t.scanning : t.scan}
                </button>
              </div>
            </form>
            <p className="scope-line">
              <ShieldCheck size={16} />
              {t.ready}
            </p>
          </div>
          <figure className="hero-visual">
            <img src="/cardsight-board.png" alt={t.imageAlt} />
          </figure>
        </section>

        {error ? (
          <div className="error-banner" role="alert">
            <AlertTriangle size={18} />
            {error}
          </div>
        ) : null}

        <section className="score-row" aria-label="Preview summary">
          <ScoreCard audit={audit} label={t.score} />
          <MetricCard icon={<Globe2 size={18} />} label="HTTP" value={audit.fetch.status ? String(audit.fetch.status) : "N/A"} detail={audit.finalUrl} level={audit.fetch.ok ? "pass" : "fail"} />
          <MetricCard icon={<ImageIcon size={18} />} label="Image" value={audit.image.width && audit.image.height ? `${audit.image.width}x${audit.image.height}` : "Unknown"} detail={audit.image.contentType || audit.image.error} level={audit.image.ok ? (audit.image.width && audit.image.width >= 1200 ? "pass" : "warn") : "fail"} />
          <MetricCard icon={<Tags size={18} />} label="OG" value={audit.tags.ogTitle ? "Present" : "Missing"} detail={audit.tags.ogType || "type missing"} level={audit.tags.ogTitle && audit.tags.ogImage ? "pass" : "warn"} />
        </section>

        <section className="workspace">
          <div className="main-column">
            <Panel title={t.cards} icon={<MonitorSmartphone size={18} />}>
              <PreviewCards title={title} description={description} imageUrl={audit.image.url} site={audit.tags.ogSiteName || audit.hostname} />
            </Panel>

            <div className="split-grid">
              <Panel title={t.image} icon={<FileImage size={18} />}>
                <ImageDiagnostics audit={audit} />
              </Panel>
              <Panel title={t.tags} icon={<Tags size={18} />}>
                <TagFacts tags={audit.tags} />
              </Panel>
            </div>

            <Panel title={t.checks} icon={<BadgeCheck size={18} />}>
              <CheckList checks={audit.checks} />
            </Panel>
          </div>

          <aside className="side-column">
            <Panel title={t.output} icon={<Code2 size={18} />}>
              <div className="tab-row" role="tablist" aria-label="Generated output">
                <button type="button" className={activeOutput === "meta" ? "active" : ""} onClick={() => setActiveOutput("meta")}>
                  {t.meta}
                </button>
                <button type="button" className={activeOutput === "markdown" ? "active" : ""} onClick={() => setActiveOutput("markdown")}>
                  {t.markdown}
                </button>
              </div>
              <div className="code-box">
                <pre>{output}</pre>
                <CopyButton value={output} copyLabel={t.copy} copiedLabel={t.copied} />
              </div>
            </Panel>

            <Panel title={t.issues} icon={<AlertTriangle size={18} />}>
              <IssueList issues={priorityIssues} emptyText={t.noIssues} />
            </Panel>

            <aside className="cache-note">
              <strong>{t.noteTitle}</strong>
              <p>{t.noteBody}</p>
              <a data-google-vignette="false" href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noreferrer">
                Meta debugger
                <ExternalLink size={14} />
              </a>
              <a data-google-vignette="false" href="https://www.linkedin.com/post-inspector/" target="_blank" rel="noreferrer">
                LinkedIn inspector
                <ExternalLink size={14} />
              </a>
            </aside>
          </aside>
        </section>
      </section>
    </main>
  );
}

function ScoreCard({ audit, label }: { audit: CardPreviewAudit; label: string }) {
  const angle = Math.max(0, Math.min(100, audit.score)) * 3.6;
  return (
    <article className="score-card">
      <div className="score-ring" style={{ "--angle": `${angle}deg` } as React.CSSProperties}>
        <span>{audit.score}</span>
      </div>
      <div>
        <p>{label}</p>
        <strong>{audit.scoreLabel}</strong>
        <span>{audit.summary.passes} pass / {audit.summary.warnings} warn / {audit.summary.failures} fail</span>
      </div>
    </article>
  );
}

function MetricCard({ icon, label, value, detail, level }: { icon: React.ReactNode; label: string; value: string; detail: string; level: Level }) {
  return (
    <article className={`metric-card ${level}`}>
      <div className="metric-icon">{icon}</div>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        <span>{detail}</span>
      </div>
    </article>
  );
}

function Panel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="panel">
      <header>
        <div>
          {icon}
          <h2>{title}</h2>
        </div>
      </header>
      {children}
    </section>
  );
}

function PreviewCards({ title, description, imageUrl, site }: { title: string; description: string; imageUrl: string; site: string }) {
  const image = imageUrl || "/cardsight-board.png";
  const onImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    event.currentTarget.src = "/cardsight-board.png";
  };

  return (
    <div className="preview-grid">
      <article className="share-card wide-card">
        <img src={image} alt="" onError={onImageError} />
        <div>
          <span>{site}</span>
          <strong>{title}</strong>
          <p>{description}</p>
        </div>
      </article>
      <article className="share-card square-card">
        <img src={image} alt="" onError={onImageError} />
        <div>
          <strong>{title}</strong>
          <p>{site}</p>
        </div>
      </article>
      <article className="message-card">
        <MessageSquare size={18} />
        <div>
          <strong>{title}</strong>
          <p>{description}</p>
          <span>{site}</span>
        </div>
      </article>
    </div>
  );
}

function ImageDiagnostics({ audit }: { audit: CardPreviewAudit }) {
  const rows = [
    ["URL", audit.image.url || "Missing"],
    ["HTTP", audit.image.status ? String(audit.image.status) : "N/A"],
    ["Content type", audit.image.contentType || "Unknown"],
    ["Dimensions", audit.image.width && audit.image.height ? `${audit.image.width}x${audit.image.height}` : "Unknown"],
    ["Ratio", audit.image.ratio],
    ["Bytes", audit.image.bytes ? formatBytes(audit.image.bytes) : "Unknown"],
    ["HTTPS", audit.image.https ? "Yes" : "No"]
  ];

  return (
    <div className="fact-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function TagFacts({ tags }: { tags: TagMap }) {
  const rows = [
    ["og:title", tags.ogTitle],
    ["og:description", tags.ogDescription],
    ["og:image", tags.ogImage],
    ["og:url", tags.ogUrl],
    ["og:type", tags.ogType],
    ["twitter:card", tags.twitterCard],
    ["title", tags.title],
    ["description", tags.description],
    ["canonical", tags.canonical]
  ];

  return (
    <div className="fact-list">
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value || "Missing"}</strong>
        </div>
      ))}
    </div>
  );
}

function CheckList({ checks }: { checks: PreviewCheck[] }) {
  return (
    <div className="check-list">
      {checks.map((check) => (
        <article className={`check-row ${check.level}`} key={check.key}>
          <LevelIcon level={check.level} />
          <div>
            <p>{check.group}</p>
            <strong>{check.label}</strong>
            <span>{check.detail}</span>
          </div>
          <em>{check.value}</em>
        </article>
      ))}
    </div>
  );
}

function IssueList({ issues, emptyText }: { issues: PreviewIssue[]; emptyText: string }) {
  if (!issues.length) {
    return (
      <div className="empty-state">
        <CheckCircle2 size={18} />
        {emptyText}
      </div>
    );
  }

  return (
    <div className="issue-list">
      {issues.map((issue) => (
        <article className={`issue-card ${issue.level}`} key={`${issue.group}-${issue.title}`}>
          <LevelIcon level={issue.level} />
          <div>
            <strong>{issue.title}</strong>
            <span>{issue.group}</span>
            <p>{issue.detail}</p>
          </div>
        </article>
      ))}
    </div>
  );
}

function LevelIcon({ level }: { level: Level }) {
  if (level === "pass") return <CheckCircle2 className="level pass" size={18} />;
  if (level === "fail") return <XCircle className="level fail" size={18} />;
  if (level === "warn") return <AlertTriangle className="level warn" size={18} />;
  return <Sparkles className="level info" size={18} />;
}

function CopyButton({ value, copyLabel, copiedLabel }: { value: string; copyLabel: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1300);
  }

  return (
    <button className="copy-button" type="button" onClick={copy}>
      <Copy size={15} />
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

function formatBytes(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${Math.round(bytes / 1000)} KB`;
}

createRoot(document.getElementById("root")!).render(<App />);
